import React from 'react';
import { Box, Flex, Paper, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { PerformanceReviewRoundStage } from '../../../../api/generated/model/performanceReviewRoundStage';
import { Eyebrow } from '../../parts/primitives';

interface StageHistoryEntry {
  stage?: PerformanceReviewRoundStage;
  transitionedAt?: string | Date;
  transitionedBy?: string | { name?: string };
  mode?: 'manual' | 'auto-completion';
}

interface StageHistoryCardProps {
  history: StageHistoryEntry[];
  stageLabel: (s: string) => string;
  locale: string;
  userNameById?: Record<string, string>;
}

const formatDateTime = (d: string | Date | undefined, locale: string) => {
  if (!d) return '';
  const dt = typeof d === 'string' ? new Date(d) : d;
  return (
    dt.toLocaleDateString(locale, { day: 'numeric', month: 'short' }) +
    ' · ' +
    dt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  );
};

export const StageHistoryCard: React.FC<StageHistoryCardProps> = ({
  history,
  stageLabel,
  locale,
  userNameById,
}) => {
  const { t } = useTranslation();
  const sorted = [...history].sort(
    (a, b) =>
      +new Date(a.transitionedAt as string | Date) - +new Date(b.transitionedAt as string | Date),
  );

  return (
    <Paper withBorder radius="lg" p="md">
      <Eyebrow>{t('perfReview.adminRound.stageHistory', 'История стадий')}</Eyebrow>
      {sorted.length === 0 ? (
        <Text c="dimmed" size="sm" mt="sm">
          {t('perfReview.adminRound.stageHistoryEmpty', 'Стадии пока не переключались')}
        </Text>
      ) : (
        <Stack gap={0} mt="sm" style={{ position: 'relative' }}>
          {sorted.map((h, i) => {
            const by =
              typeof h.transitionedBy === 'object' && h.transitionedBy
                ? h.transitionedBy.name
                : typeof h.transitionedBy === 'string'
                  ? userNameById?.[h.transitionedBy] || h.transitionedBy
                  : '—';
            const isLast = i === sorted.length - 1;
            return (
              <Flex
                key={i}
                gap="sm"
                align="flex-start"
                pb={isLast ? 0 : 'md'}
                style={{ position: 'relative', minHeight: 28 }}
              >
                {!isLast && (
                  <Box
                    style={{
                      position: 'absolute',
                      left: 5,
                      top: 16,
                      bottom: 4,
                      width: 2,
                      background: 'var(--mantine-color-default-border)',
                    }}
                  />
                )}
                <Box
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 999,
                    background: 'var(--mantine-primary-color-filled)',
                    border: '2px solid var(--mantine-primary-color-filled)',
                    marginTop: 4,
                    flexShrink: 0,
                    zIndex: 1,
                  }}
                />
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500}>
                    {h.stage ? stageLabel(h.stage) : '—'}
                  </Text>
                  <Text size="xs" c="dimmed" mt={2} style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatDateTime(h.transitionedAt, locale)}
                    {by ? ` · ${by}` : ''}
                  </Text>
                  {h.mode === 'auto-completion' && (
                    <Text size="xs" c="dimmed" mt={2} fs="italic">
                      {t('perfReview.adminRound.autoTransition', 'автоматический переход')}
                    </Text>
                  )}
                </Box>
              </Flex>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};
